import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import * as JspUtil from '../common/JspUtil';
import submitHiddenForm from '../common/submitHiddenForm';
import LoginTemplate from './LoginTemplate';
import { getTfaDetailsAuthenticatedAsync } from '../tfapages/actionCreators';
import { IceIcon, PageContainer } from '../components';
import {
    EMAIL_DELIVERY, GOOGLE_AUTH_DELIVERY, SAVED_AUTH_PARAM, TFA_STATE,
    USER_INPUT_TFA_DELIVERY_PREFERENCE_CHANGE, WEBAUTHN_DELIVERY
} from './actionCreators';
import { useJspVariables, useAuth, useLoginFlowState, useDebugRender, useLoginInput } from '../hooks';

const PageAppUserLoginTfa = () => {
    useDebugRender('PageAppUserLoginTfa');
    const dispatch = useDispatch();
    const location = useLocation();
    const { launcher, panelStyle } = useJspVariables();

    const { isAuthenticated } = useAuth();
    const { setUserName } = useLoginInput();
    const { webauthnDetails } = useLoginFlowState();
    const [userTfaDetails, setUserTfaDetails] = useState({});
    const [alert, setAlert] = useState(null);

    const [pageMessage] = useState(() => {
        return location?.state?.pageMessage || '';
    });
    const redirectUrl = JspUtil.isValueAvailable(window.jspRedirectUrl) ? window.jspRedirectUrl : '';
    const loginApp = JspUtil.isValueAvailable(window.jspLoginApp) ? window.jspLoginApp : '';
    const [tfaPrompt, setTfaPrompt] = useState('');
    const [isTfaRegForward, setIsTfaRegForward] = useState('N');
    const [otpCode, setOtpCode] = useState('');

    const submitLoginForm = () => {
        submitHiddenForm('/appUserLogin', {
            redirectUrl,
            loginApp,
            otpCode,
            webauthnApp: webauthnDetails.appKey,
            webauthnPublicKey: webauthnDetails.publicKey,
            webauthnRequestId: webauthnDetails.requestId,
            webauthnUserName: webauthnDetails.userName,
            isTfaRegForward,
            samlSession: JspUtil.isValueAvailable(window.jspSamlSession) ? window.jspSamlSession : undefined,
        });
    };

    useEffect(() => {
        if (JspUtil.isValueAvailable(window.jspTfaPromptMessage)) {
            setTfaPrompt(window.jspTfaPromptMessage);
        }
        if (JspUtil.isValueAvailable(window.jspIsTfaRegForward)) {
            setIsTfaRegForward(window.jspIsTfaRegForward);
        }

        if (!userTfaDetails || !userTfaDetails.user) {
            getTfaDetailsAuthenticatedAsync(true).then(resp => {
                setUserTfaDetails(resp.data);
            }).catch(err => console.error('getTfaDetailsAuthenticated failed:', err));
        }
    }, []);

    useEffect(() => {
        if (webauthnDetails && webauthnDetails.requestId) {
            submitLoginForm();
        }
    }, [webauthnDetails]);

    const onLoginBtnClick = () => {
        setAlert(null);
        submitLoginForm();
    };

    let tfaDeliveryPref = EMAIL_DELIVERY;
    if (userTfaDetails.isActiveTfaMobile && userTfaDetails.isActiveTfaMobile === 'Y') {
        tfaDeliveryPref = GOOGLE_AUTH_DELIVERY;
    }
    if (isAuthenticated) {
        if (userTfaDetails.isActiveTfaWebAuthn && userTfaDetails.isActiveTfaWebAuthn === 'Y') {
            tfaDeliveryPref = WEBAUTHN_DELIVERY;
        }
    }

    useEffect(() => {
        if (userTfaDetails && userTfaDetails.user) {
            dispatch({ type: TFA_STATE, data: {
                isWebAuthnRegistered: userTfaDetails.isWebAuthnRegistered || 'N',
                isTfaDeviceRegistered: userTfaDetails.isTfaDeviceRegistered || 'N',
                prompt: tfaPrompt,
            }});
            dispatch({ type: USER_INPUT_TFA_DELIVERY_PREFERENCE_CHANGE, data: tfaDeliveryPref });
            if (userTfaDetails.tfaEmail) {
                setUserName(userTfaDetails.tfaEmail);
            }
            dispatch({ type: SAVED_AUTH_PARAM, data: { appUserLoginTfa: 'True' } });
        }
    }, [userTfaDetails, tfaPrompt]);

    return (
        <PageContainer id="pageTFAMessage" dataAutomationId="page_tfa_message">
            <Box style={panelStyle}>
                <IceIcon launcher={launcher} />
                {alert && <Alert severity={alert.severity} sx={{ mb: 1 }} onClose={() => setAlert(null)}>{alert.message}</Alert>}
                <Typography fontWeight={400} sx={{ fontSize: '1.5rem', pb: '5px', m: 0 }}>{JspUtil.getLabelFromJspVariables('title_login_requires_mfa')}</Typography>
                <Box>
                    <LoginTemplate
                        appUserLoginTfa={true}
                        onTfaOtpChange={(e) => setOtpCode(e.target.value)}
                        onLoginBtnClick={onLoginBtnClick}
                        pageMessage={pageMessage}
                    />
                </Box>
            </Box>
        </PageContainer>
    );
};

export default PageAppUserLoginTfa;
